import { createFileRoute } from "@tanstack/react-router"
import { lazy, useMemo } from "react"
import TabsList from "../../../components/Common/TabsList"
import { userHasPermission } from "../../../utils"
import { NoPermission } from "../../../components/UI/no-permission"
import { useQuery } from "@tanstack/react-query"
import { readRatecharts } from "../../../api/ratecharts"
import Navbar from "../../../components/Common/Navbar"
import { Link, SkeletonText, Table } from "@chakra-ui/react"
import ActionsMenu from "../../../components/Common/ActionsMenu"

const AddSettingsRatechart = lazy(() => import("../../../components/Ratecharts/AddSettingsRatechart"))

export const Route = createFileRoute("/_layout/settings/ratecharts")({
  component: Ratecharts,
})

function RatechartsTable() {
    // Permission check
    const [rc_read, rc_create, rc_update, rc_delete] = useMemo(() => {
        return ["read", "create", "update", "delete"].map(
            (action) => userHasPermission([{ perm: `settings_rates_${action}`, is_allowed: false }])[0].is_allowed
        )
    }, [userHasPermission])

    if(!rc_read) return <NoPermission/>

    const fields = [
        { title: "Titel", col: null, align: undefined },
        { title: "Dateiname", col: null, align: undefined },
        { title: "Erstellungsdatum", col: null, align: undefined },
        { title: "Aktionen", col: null, align: "right" as const },
    ]

    // Query
    const {
        data: ratecharts,
        isPending,
        isPlaceholderData,
    } = useQuery({
        ...readRatecharts(),
        placeholderData: (prevData) => prevData,
    })

    return (
        <>
            <Navbar
                type="Tariftabelle"
                addAllowed={rc_create}
                addModalAs={AddSettingsRatechart}
                showSearch={false}
            />

            <Table.ScrollArea asChild>
                <Table.Root size={{ base: "sm", md: "md" }} whiteSpace="normal" display={{md: "table"}}>
                <Table.Header>
                    <Table.Row>
                    {fields.map((field) =>
                        <Table.ColumnHeader textAlign={field.align} key={field.title}>
                            {field.title}
                        </Table.ColumnHeader>
                    )}
                    </Table.Row>
                </Table.Header>
                {isPending ? (
                    <Table.Body>
                    <Table.Row>
                        {new Array(4).fill(null).map((_, index) => (
                        <Table.Cell key={index}>
                            <SkeletonText noOfLines={1} paddingBlock="16px" />
                        </Table.Cell>
                        ))}
                    </Table.Row>
                    </Table.Body>
                ) : (
                    <Table.Body>
                    {!ratecharts?.data ? (
                        <Table.Row>
                        <Table.Cell colSpan={4} textAlign="center">
                            Keine Tariftabellen verfügbar
                        </Table.Cell>
                        </Table.Row>
                    ) : (
                        ratecharts?.data.map((item) => (
                            <Table.Row key={item.id} opacity={isPlaceholderData ? 0.5 : 1}>
                                <Table.Cell>
                                    {item.label}
                                </Table.Cell>

                                <Table.Cell>
                                    <Link href={item.fk_media.path} target="_blank">
                                        {item.fk_media.file_name}
                                    </Link>
                                </Table.Cell>

                                <Table.Cell>
                                    {new Date(`${item.fk_media.created_at}`).toLocaleString("de-CH", {
                                        timeZone: "Europe/Zurich",
                                    })}
                                </Table.Cell>

                                <Table.Cell textAlign="right">
                                    <ActionsMenu
                                        type="Tariftabelle"
                                        value={item}
                                        disableDelete={!rc_delete}
                                        hideEdit={false}
                                        disableEdit={!rc_update}
                                    />
                                </Table.Cell>
                            </Table.Row>
                        ))
                    )}
                    </Table.Body>
                )}
                </Table.Root>
            </Table.ScrollArea>
        </>
    )
}

export default RatechartsTable

function Ratecharts() {
    const tabs = useMemo(() => [
        { value: "settings", title: "Allgemein" },
        { value: "settings/navigation", title: "Navigation" },
        { value: "settings/landingpage", title: "Startseite" },
        { value: "settings/mimetypes", title: "Dateitypen" },
        { value: "settings/subscriptions", title: "Abos" },
        { value: "settings/priocategories", title: "Kategorien" },
        { value: "settings/ratecharts", title: "Tariftabellen" },
        { value: "settings/actions", title: "Aktionen" },
    ], [])

    return <TabsList tabs={tabs} defaultVal="settings" />
}
